import type { ReactNode } from 'react';
import { Icon } from '../common/Icon';

/**
 * CommunityCard — the canonical card shape for an Alive community
 * (or any first-class entity: a project, a team, a company-page-like
 * aggregation). The visual language is borrowed from LinkedIn's
 * company card: banner header → overlapping square logo → name +
 * tagline → metadata row → join/visit action.
 *
 * Unlike a `MasonryCard` (which is a stream of content), a
 * `CommunityCard` represents a *destination* — somewhere the user
 * will navigate INTO. The card is taller, has a strong header
 * identity, and earns its visual weight through the banner rather
 * than a flat background fill.
 */
export interface CommunityCardProps {
  /** Banner image URL — the wide cover image at the top. ~3:1 to
   * ~4:1 aspect ratio works best. If omitted, falls back to a soft
   * gradient using the category accent color. */
  banner?: string;
  /** Optional Tailwind class string applied to the banner element
   * when there's no image — usually a gradient like
   * `bg-gradient-to-br from-primary/30 to-primary/5`. */
  bannerClassName?: string;
  /** Square logo / icon. URL renders as an `<img>`; ReactNode
   * renders as-is (use this when you want a Material Symbol icon
   * tile or a custom badge). 64×64 by default, sits half-overlapping
   * the banner's bottom edge. */
  logo: string | ReactNode;
  /** Display name. Plain text or ReactNode for inline badges. */
  name: ReactNode;
  /** Optional one-line tagline shown directly under the name. */
  tagline?: ReactNode;
  /** Metadata row — typically 2-4 small icon+text pairs (members,
   * agents, location, category). Use `<CommunityMeta>` items inside. */
  meta?: ReactNode;
  /** Free-form tag chips below the metadata. Each tag is a string. */
  tags?: string[];
  /** Latest activity preview at the bottom of the card — usually a
   * single-line excerpt of the most recent post. */
  latestActivity?: ReactNode;
  /** Action area on the bottom right — typically a "Follow" or
   * "Join" or "Visit" button. */
  action?: ReactNode;
  /** Tap handler for the whole card. */
  onClick?: () => void;
}

export function CommunityCard({
  banner,
  bannerClassName = 'bg-gradient-to-br from-primary/30 via-primary/15 to-primary/5',
  logo,
  name,
  tagline,
  meta,
  tags,
  latestActivity,
  action,
  onClick,
}: CommunityCardProps) {
  return (
    <article
      onClick={onClick}
      className={`group ${onClick ? 'cursor-pointer' : ''}`}
    >
      {/* Banner — fixed height, rounded top corners, gradient when no image */}
      <div className={`relative h-24 rounded overflow-hidden ${banner ? '' : bannerClassName}`}>
        {banner && (
          <img
            src={banner}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {/* Logo + identity — logo overlaps the banner bottom edge */}
      <div className="px-3 -mt-7 relative">
        <div className="flex items-end gap-3">
          {/* Logo tile — 64×64, sits half over the banner. Has a
              ring of the page background color so it visually
              "punches through" the banner edge. */}
          <div className="flex-shrink-0 w-14 h-14 rounded bg-white dark:bg-[#0B0B0D] p-0.5 ring-2 ring-white dark:ring-[#0B0B0D]">
            <div className="w-full h-full rounded overflow-hidden flex items-center justify-center bg-gray-100 dark:bg-white/[0.05]">
              {typeof logo === 'string' ? (
                <img src={logo} alt="" className="w-full h-full object-cover" />
              ) : (
                logo
              )}
            </div>
          </div>

          {/* Action sits opposite the logo on the right, aligned to
              its bottom so it nests against the banner edge. */}
          {action && <div className="flex-shrink-0 mb-0.5 ml-auto">{action}</div>}
        </div>

        {/* Identity block — name + tagline. Padded so it aligns
            with the start of the logo column above it. */}
        <div className="mt-2 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
            {name}
          </h3>
          {tagline && (
            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5 leading-snug">
              {tagline}
            </p>
          )}
        </div>

        {/* Metadata row + tags */}
        {(meta || (tags && tags.length > 0)) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {meta}
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/[0.05] text-gray-500 dark:text-gray-400"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Latest activity preview — sits at the bottom with a
            hairline above it to separate it from the identity block. */}
        {latestActivity && (
          <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-white/[0.06]">
            {latestActivity}
          </div>
        )}
      </div>
    </article>
  );
}

// ─── CommunityMeta — single icon+text metadata cell ───────────────────────

export interface CommunityMetaProps {
  icon: string;
  children: ReactNode;
}

/**
 * One small metadata pair inside a `CommunityCard`'s `meta` slot —
 * an icon + a number/label. Use freely; the parent row wraps so a
 * card can have 2, 3, 4, or more without breaking layout.
 *
 * @example
 * <CommunityCard
 *   meta={
 *     <>
 *       <CommunityMeta icon="person">342 members</CommunityMeta>
 *       <CommunityMeta icon="smart_toy">289 agents</CommunityMeta>
 *       <CommunityMeta icon="forum">12 active</CommunityMeta>
 *     </>
 *   }
 * />
 */
export function CommunityMeta({ icon, children }: CommunityMetaProps) {
  return (
    <span className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
      <Icon name={icon} size={12} className="text-gray-400 dark:text-gray-500" />
      {children}
    </span>
  );
}
